import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  Box,
  Button,
  Typography,
  Card,
  CardContent,
  Paper,
  Container,
  Grid,
  Divider,
  CircularProgress,
  Alert,
  Checkbox,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  alpha,
} from "@mui/material";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";

// Icons
import ReceiptLongIcon from "@mui/icons-material/ReceiptLong";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";

// Services
import * as saleOrderService from "../services/saleOrderService";

// Components
import SaleOrderStatusChip from "../components/SaleOrderStatusChip";

function ConvertToInvoicePage() {
  const [searchParams] = useSearchParams();
  const saleOrderId = searchParams.get('id');
  const navigate = useNavigate();
  const { t } = useTranslation();
  
  // State
  const [saleOrder, setSaleOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [converting, setConverting] = useState(false);
  const [selectedLineItems, setSelectedLineItems] = useState([]);

  // Load sale order
  useEffect(() => {
    if (!saleOrderId) {
      toast.error(t('sale_order_id_missing'));
      navigate('/sales/sale-orders');
      return;
    }
    loadSaleOrder();
  }, [saleOrderId]);

  const loadSaleOrder = async () => {
    setLoading(true);
    try {
      const response = await saleOrderService.getSaleOrderById(saleOrderId);
      const data = response?.payload || response?.data || response;
      
      // Check if already converted
      if (data.InvoiceId || data.InvoiceNumber) {
        toast.warning(t('sale_order_already_converted'));
        navigate(`/sales/sale-orders/${saleOrderId}`);
        return;
      }
      
      // Check if closed
      if (data.Status?.toLowerCase() !== 'closed') {
        toast.warning(t('sale_order_must_be_closed'));
        navigate(`/sales/sale-orders/${saleOrderId}`);
        return;
      }
      
      setSaleOrder(data);
      
      // Select all line items by default
      if (data.LineItems && data.LineItems.length > 0) {
        setSelectedLineItems(data.LineItems.map((item, index) => index));
      }
    } catch (error) {
      console.error('Error loading sale order:', error);
      toast.error(t('failed_to_load_sale_order'));
      navigate('/sales/sale-orders');
    } finally {
      setLoading(false);
    }
  };

  // Handle line item toggle
  const handleLineItemToggle = (index) => {
    setSelectedLineItems(prev => {
      if (prev.includes(index)) {
        return prev.filter(i => i !== index);
      } else {
        return [...prev, index];
      }
    });
  };

  // Handle select all
  const handleSelectAll = (checked) => {
    if (checked) {
      setSelectedLineItems(saleOrder.LineItems.map((item, index) => index));
    } else {
      setSelectedLineItems([]);
    }
  };

  // Handle convert to invoice
  const handleConvertToInvoice = async () => {
    if (selectedLineItems.length === 0) {
      toast.warning(t('please_select_at_least_one_item'));
      return;
    }

    setConverting(true);
    try {
      // Get selected line item IDs
      const selectedItemIds = selectedLineItems.map(index => 
        saleOrder.LineItems[index].Id
      );

      // Prepare DTO matching backend ConvertSaleOrderToInvoiceDto
      const convertDto = {
        LineItemIds: selectedItemIds,
        // Optional fields - can be extended later:
        // IssueDate: null,
        // PaymentDueDate: null,
        // InvoiceNotes: null
      };

      // Call backend to convert
      const response = await saleOrderService.convertSaleOrderToInvoice(saleOrderId, convertDto);
      
      const invoice = response?.payload || response?.data || response;
      // Handle case where payload is directly the invoice ID (number)
      const invoiceId = typeof invoice === 'number' ? invoice : (invoice?.Id || invoice?.id || invoice?.InvoiceId);

      if (!invoiceId) {
        throw new Error('Invoice ID not returned from backend');
      }

      toast.success(t('draft_invoice_created') || 'Draft invoice created successfully');
      
      // Navigate to invoice edit page
      navigate(`/invoices/edit/${invoiceId}`);
    } catch (error) {
      console.error('Error converting to invoice:', error);
      toast.error(t('failed_to_convert_to_invoice') || 'Failed to convert to invoice. Please try again.');
    } finally {
      setConverting(false);
    }
  };

  if (loading) {
    return (
      <Container maxWidth="xl" sx={{ mt: 4, mb: 4, display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
        <CircularProgress size={60} />
      </Container>
    );
  }

  if (!saleOrder) {
    return null;
  }

  const lineItems = saleOrder.LineItems || [];
  const customer = saleOrder.Customer || {};
  
  // Calculate totals for selected items
  const selectedTotal = selectedLineItems.reduce((sum, index) => {
    const item = lineItems[index];
    return sum + Number(item.TotalIncl || 0);
  }, 0);

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
      {/* Header */}
      <Paper 
        elevation={0} 
        sx={{ 
          p: 3, 
          mb: 3, 
          background: 'linear-gradient(135deg, #2196f3 0%, #1565c0 100%)',
          color: 'white',
          borderRadius: 2,
        }}
      >
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <ReceiptLongIcon sx={{ fontSize: 40 }} />
            <Box>
              <Typography variant="h4" fontWeight={700}>
                {t('convert_to_invoice')}
              </Typography>
              <Typography variant="body2" sx={{ opacity: 0.9, mt: 0.5 }}>
                {t('sale_order')}: {saleOrder.SaleOrderNumber}
              </Typography>
            </Box>
          </Box>
          <Button
            variant="contained"
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate(`/sales/sale-orders/${saleOrderId}`)}
            sx={{
              bgcolor: 'white',
              color: '#2196f3',
              fontWeight: 700,
              '&:hover': { bgcolor: '#f5f5f5' },
            }}
          >
            {t('back')}
          </Button>
        </Box>
      </Paper>

      {/* Info Alert */}
      <Alert severity="info" sx={{ mb: 3 }}>
        <Typography variant="body2" fontWeight={600}>
          {t('select_items_to_convert') || 'Select items to convert to invoice'}
        </Typography>
        <Typography variant="caption">
          {t('items_from_sale_order_note') || 'Note: Items from sale order will not affect inventory as it was already adjusted.'}
        </Typography>
      </Alert>

      <Grid container spacing={3}>
        {/* Left Column - Line Items with Checkboxes */}
        <Grid item xs={12} md={8}>
          {/* Customer Info */}
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" fontWeight={600} gutterBottom>
                {t('customer')}
              </Typography>
              <Divider sx={{ mb: 2 }} />
              <Typography variant="body2" fontWeight={600}>
                {customer.CompanyName || customer.Name}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {customer.Email}
              </Typography>
            </CardContent>
          </Card>

          {/* Line Items with Checkboxes */}
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6" fontWeight={600}>
                  {t('line_items')}
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Checkbox
                    checked={selectedLineItems.length === lineItems.length}
                    onChange={(e) => handleSelectAll(e.target.checked)}
                    sx={{ '&.Mui-checked': { color: '#2196f3' } }}
                  />
                  <Typography variant="body2" fontWeight={600}>
                    {t('select_all')} ({selectedLineItems.length}/{lineItems.length})
                  </Typography>
                </Box>
              </Box>
              <Divider sx={{ mb: 2 }} />
              
              <Box sx={{ overflowX: 'auto' }}>
                <Table>
                  <TableHead>
                    <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                      <TableCell padding="checkbox">
                        <Checkbox
                          checked={selectedLineItems.length === lineItems.length}
                          onChange={(e) => handleSelectAll(e.target.checked)}
                          sx={{ '&.Mui-checked': { color: '#2196f3' } }}
                        />
                      </TableCell>
                      <TableCell>{t('product')}</TableCell>
                      <TableCell align="center">{t('quantity')}</TableCell>
                      <TableCell align="right">{t('unit_price')}</TableCell>
                      <TableCell align="right">{t('total_excl_vat')}</TableCell>
                      <TableCell align="right">{t('vat')}</TableCell>
                      <TableCell align="right">{t('total_incl_vat')}</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {lineItems.map((item, index) => {
                      const isSelected = selectedLineItems.includes(index);
                      return (
                        <TableRow 
                          key={index}
                          sx={{
                            bgcolor: isSelected ? alpha('#2196f3', 0.08) : 'transparent',
                            cursor: 'pointer',
                            '&:hover': { bgcolor: isSelected ? alpha('#2196f3', 0.12) : alpha('#000', 0.04) }
                          }}
                          onClick={() => handleLineItemToggle(index)}
                        >
                          <TableCell padding="checkbox">
                            <Checkbox
                              checked={isSelected}
                              onChange={() => handleLineItemToggle(index)}
                              sx={{ '&.Mui-checked': { color: '#2196f3' } }}
                              onClick={(e) => e.stopPropagation()}
                            />
                          </TableCell>
                          <TableCell>{item.Name || item.ProductName}</TableCell>
                          <TableCell align="center">{item.Quantity}</TableCell>
                          <TableCell align="right">€{Number(item.UnitPrice || 0).toFixed(2)}</TableCell>
                          <TableCell align="right">€{Number(item.TotalExcl || 0).toFixed(2)}</TableCell>
                          <TableCell align="right">€{Number(item.VATAmount || 0).toFixed(2)}</TableCell>
                          <TableCell align="right" sx={{ fontWeight: 600 }}>
                            €{Number(item.TotalIncl || 0).toFixed(2)}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Right Column - Summary & Action */}
        <Grid item xs={12} md={4}>
          <Card sx={{ position: 'sticky', top: 20 }}>
            <CardContent>
              <Typography variant="h6" fontWeight={600} gutterBottom>
                {t('summary')}
              </Typography>
              <Divider sx={{ mb: 2 }} />
              
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="body2" color="text.secondary">
                    {t('selected_items')}:
                  </Typography>
                  <Typography variant="body2" fontWeight={600}>
                    {selectedLineItems.length} / {lineItems.length}
                  </Typography>
                </Box>
                
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="body2" color="text.secondary">
                    {t('vat_percentage')}:
                  </Typography>
                  <Typography variant="body2" fontWeight={600}>
                    {saleOrder.VATPercentage || 0}%
                  </Typography>
                </Box>
                
                <Divider />
                
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="h6" fontWeight={700}>
                    {t('total')}:
                  </Typography>
                  <Typography variant="h6" fontWeight={700} color="primary">
                    €{selectedTotal.toFixed(2)}
                  </Typography>
                </Box>

                <Divider />

                <Alert severity="warning" sx={{ mt: 1 }}>
                  <Typography variant="caption">
                    {t('draft_invoice_will_be_created') || 'A draft invoice will be created. You can edit it before sending.'}
                  </Typography>
                </Alert>

                <Button
                  variant="contained"
                  fullWidth
                  size="large"
                  startIcon={<CheckCircleIcon />}
                  onClick={handleConvertToInvoice}
                  disabled={converting || selectedLineItems.length === 0}
                  sx={{
                    mt: 2,
                    py: 1.5,
                    bgcolor: '#4caf50',
                    fontWeight: 700,
                    fontSize: '1rem',
                    '&:hover': { bgcolor: '#388e3c' }
                  }}
                >
                  {converting 
                    ? t('creating_invoice') || 'Creating Invoice...' 
                    : t('create_draft_invoice') || 'Create Draft Invoice'}
                </Button>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Container>
  );
}

export default ConvertToInvoicePage;
